import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLogout, useSession } from '../lib/auth-store.js';
import { useTheme } from '../lib/theme.js';

/**
 * Compact user menu sitting in the top nav. Shows the user's display name
 * (falling back to email when the user hasn't set a name yet) and opens
 * a dropdown with Settings / Theme toggle / Sign out.
 *
 * Designed for both desktop and mobile (the dropdown is also reachable
 * from the mobile drawer; that copy just renders the same items inline).
 */
export function UserMenu(): JSX.Element | null {
  const session = useSession();
  const logout = useLogout();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const user = session.data?.user;
  if (!user) return null;

  const displayName = user.name?.trim() || user.email;
  const initials = initialsFor(displayName);

  const onSignOut = async (): Promise<void> => {
    setOpen(false);
    await logout();
    navigate('/login');
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-full border border-border bg-surface-alt px-2 py-1 text-sm hover:bg-surface"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span
          className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent/15 text-accent text-xs font-semibold"
          aria-hidden
        >
          {initials}
        </span>
        <span className="hidden sm:inline max-w-[12rem] truncate" title={displayName}>
          {displayName}
        </span>
        <svg className="h-3 w-3 opacity-60" viewBox="0 0 12 12" aria-hidden>
          <path d="M3 4.5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-56 rounded-md border border-border bg-surface-alt shadow-lg z-50"
        >
          <div className="border-b border-border px-3 py-2">
            <div className="truncate text-sm font-medium" title={displayName}>{displayName}</div>
            {user.name && (
              <div className="truncate text-xs text-ink-muted" title={user.email}>{user.email}</div>
            )}
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); navigate('/settings'); }}
            className="block w-full px-3 py-2 text-left text-sm hover:bg-surface"
          >
            Settings
          </button>
          <div className="border-t border-border px-3 py-2 text-xs text-ink-muted">Theme</div>
          <div className="flex gap-1 px-2 pb-2">
            {(['light', 'dark', 'system'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTheme(t)}
                className={
                  'flex-1 rounded px-2 py-1 text-xs ' +
                  (theme === t
                    ? 'bg-accent text-white'
                    : 'bg-surface hover:bg-border')
                }
              >
                {t}
              </button>
            ))}
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => { void onSignOut(); }}
            className="block w-full border-t border-border px-3 py-2 text-left text-sm text-danger hover:bg-surface"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function initialsFor(s: string): string {
  const parts = s.split(/[\s@.]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}
